import { Injectable, inject } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { signal, effect } from "@angular/core";
import { fromEvent, merge, timer, Subscription } from "rxjs";
import { debounceTime, tap, filter } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { ApiService } from "./api.service";
import { MatDialog } from "@angular/material/dialog";
import { GroupResolverService } from "./group-resolver.service";

@Injectable({
  providedIn: "root",
})
export class InactivityService {
  private router = inject(Router);
  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private dialog = inject(MatDialog); //  Inject MatDialog to close modals
  private groupResolver = inject(GroupResolverService);
  //private inactivityTimeout = 20 * 1000; // 20 seconds in milliseconds
  private inactivityTimeout = 5 * 60 * 1000; // 5 minutes in milliseconds
  private lastActivity = signal(Date.now());
  private isMonitoring = signal(false);
  private userType = signal<string>(""); // Signal to store userType
  private timerSubscription: Subscription | null = null; // Store timer subscription
  private isOnLoginPage = signal(false); // Track if on login page

  constructor() {
    // Update userType from apiService.group$
    this.apiService.group$.subscribe((userGroup: any) => {
      this.userType.set(
        this.groupResolver.getPreferredUserTypeFromGroups(userGroup?.groups)
      );
    });

    // Listen to route changes to manage monitoring
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Extract base path without query parameters (e.g., /auth/login from /auth/login?showTotpSetup=false)
        const basePath = event.url.split("?")[0];
        this.isOnLoginPage.set(basePath === "/auth/login");
        if (basePath === "/auth/login") {
          this.stopMonitoring();
        } else if (!this.isOnLoginPage()) {
          this.startMonitoring();
        }
      });

    // Effect to handle logout when inactivity timeout is reached
    effect(() => {
      if (this.isMonitoring() && !this.isOnLoginPage()) {
        this.startInactivityTimer();
      } else {
        this.stopInactivityTimer();
      }
    });
  }

  // Start monitoring user activity
  startMonitoring(): void {
    if (!this.isOnLoginPage()) {
      this.isMonitoring.set(true);
      this.setupActivityListeners();
    }
  }

  // Stop monitoring user activity
  stopMonitoring(): void {
    this.isMonitoring.set(false);
  }

  // Reset the last activity timestamp
  private resetActivity(): void {
    this.lastActivity.set(Date.now());
  }

  // Set up listeners for user interactions
  private setupActivityListeners(): void {
    const activityEvents = merge(
      fromEvent(document, "mousemove"),
      fromEvent(document, "click"),
      fromEvent(document, "keydown"),
      fromEvent(document, "touchstart"),
      fromEvent(document, "scroll")
    );

    activityEvents
      .pipe(
        debounceTime(100), // Prevent excessive updates
        tap(() => this.resetActivity())
      )
      .subscribe();
  }

  // Start the inactivity timer
  private startInactivityTimer(): void {
    // Ensure any existing subscription is cleared
    this.stopInactivityTimer();

    this.timerSubscription = timer(0, 1000).subscribe(() => {
      const diff = Date.now() - this.lastActivity();
      // console.log("Inactivity seconds:", Math.floor(diff / 1000));
      if (
        this.isMonitoring() &&
        !this.isOnLoginPage() &&
        Date.now() - this.lastActivity() >= this.inactivityTimeout
      ) {
        this.logout();
      }
    });
  }

  // Stop the inactivity timer
  private stopInactivityTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  // Handle logout and navigation
  private async logout(): Promise<void> {
    try {
      // Stop the timer to prevent multiple logout calls
      this.stopInactivityTimer();
      // Close all open dialogs before navigating
      this.dialog?.closeAll();

      if (this.userType() === "prospect") {
        await this.apiService?.clearProspectSession();
      } else {
        await this.apiService?.clearCustomerSession();
      }

      this.apiService?.clearLocalState();
      await this.authService?.logout();
      this.router.navigate(["auth/login"]);
      this.stopMonitoring();
    } catch (error) {
      console.error("Error during logout:", error);

      this.apiService?.clearLocalState();
      // Still close dialogs & navigate even if session clear fails
      this.dialog?.closeAll();
      this.router.navigate(["auth/login"]);
      this.stopMonitoring();
    }
  }
}
